import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import MetricTrendChart from './components/MetricTrendChart';
import { useHrAnalyticsApi, resolveDefaultScope } from './config';

// Metrik gezgini (2026-08-03, kullanıcı onayı — "madde 1"): Metrik Kataloğu'ndaki HERHANGİ bir
// "Mevcut" metriğe tıklanınca açılan jenerik detay sayfası. Motorun hesapladığı 45 metriğin
// yalnız ~7'si özel grafiklerde görünüyordu; bu sayfa kalan hepsini TEK jenerik yüzeyle
// görünür yapar: aylık trend (tenant) + en son dönemin kapsam kırılımı (departman/lokasyon/
// pozisyon ailesi çubukları). Yeni backend ucu YOK — /v1/ha/metrics zaten her şeyi döndürüyor.
// Yoğunlaşma ailesi (Value = en büyük grubun payı, dağılım DEĞİL — bkz.
// HeadcountBreakdownComputers.cs sınıf yorumu): bu kodlarda "denge" referans çizgisi çizilir,
// çubuk değerleri "en büyük grup" bağlamıyla etiketlenir ve kazanan kategorinin ADI gösterilir
// (2026-08-03 kullanıcı geri bildirimi — çıplak yüzde okuma anahtarı olmadan anlamsız
// kalıyordu; 2026-09-03 — anahtar da yetmiyordu: "%75" hangi grupta olduğunu söylemeden
// departman karşılaştırmasını okunamaz bırakıyordu).
const CONCENTRATION_CODES = new Set([
  'demographics.gender',
  'demographics.education',
  'demographics.disability',
  'headcount.byEmploymentType',
  'headcount.byCollarType',
  'turnover.exitReason.distribution',
  'performance.score.distribution',
  'talent.ninebox.distribution',
  'talent.flightRisk.distribution',
  'onboarding.earlyLeaveRisk.distribution',
  'leave.type.distribution',
]);

// Backend'in `topCategoryKey`'ini (ham anahtar: int enum / sabit string kod) uygulamanın ZATEN
// sahip olduğu çeviri anahtarlarına bağlar. Bu ekran için TEK BİR yeni kategori çevirisi
// eklenmedi — var olanlar yeniden kullanıldı ki "Mavi Yaka" bir gün yeniden adlandırılırsa
// burası da onunla birlikte değişsin (ikinci bir sözlük iki ekranı ayrıştırırdı).
//
// Haritada OLMAYAN üç kod ve nedenleri:
//   • leave.type.distribution / performance.score.distribution — kategori tenant tanımlı bir
//     KAYITTIR (izin türü, skala seviyesi); adı backend'den `topCategoryLabel` olarak gelir.
//   • demographics.disability — engellilik seviyesi uygulamanın her yerinde ham sayı olarak
//     ("0".."3") gösterilir; burada uydurma bir etiket üretmek yerine aynı ham değer gösterilir.
const CATEGORY_LABEL_KEYS = {
  'demographics.gender': {
    1: 'employee.male', 2: 'employee.female', 3: 'employee.preferNotToSay',
  },
  'demographics.education': {
    1: 'employee.eduPrimary', 2: 'employee.eduSecondary', 3: 'employee.eduHighSchool',
    4: 'employee.eduAssociate', 5: 'employee.eduBachelor', 6: 'employee.eduMaster',
    7: 'employee.eduDoctorate',
  },
  'headcount.byCollarType': { 1: 'employee.whiteCollar', 2: 'employee.blueCollar' },
  'headcount.byEmploymentType': {
    1: 'employee.fullTime', 2: 'employee.partTime', 3: 'employee.contract',
  },
  'turnover.exitReason.distribution': (key) => `employee.exitReasons.${key}`,
  'talent.ninebox.distribution': (key) => `performance.ninebox.cell.${key}`,
  'talent.flightRisk.distribution': (key) => `performance.talentpool.flightRisk.${key}`,
  'onboarding.earlyLeaveRisk.distribution': (key) => `onboarding.riskLevel.${key}`,
};

// Bir dönem içinde `categoryCount` (k) tenant genelinde TEK bir sabittir, ama seri birden çok
// dönem taşır ve k dönemden döneme oynayabilir (ör. ayrılış nedeni çeşidi). EN SON dönemin
// k'sı alınır: "bugün denge nedir" sorusunun cevabı budur. Bastırılmış hücrelerde de dolu
// gelir (bkz. HaMetricCellResponse.CategoryCount), yani gizlenen çubuklar ölçeği bozmaz.
const latestCategoryCount = (cells) => {
  let value = null;
  let latest = -Infinity;
  cells.forEach((c) => {
    if (c?.categoryCount == null) return;
    const time = new Date(c.periodStart).getTime();
    if (time > latest) { latest = time; value = c.categoryCount; }
  });
  return value;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MetricDescription = styled.p`
  margin: 10px 0 0 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 760px;
  line-height: 1.5;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  padding: 20px 24px;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetaChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => `${theme.colors.primary.main}22`};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  max-width: 280px;
`;

// ── Kırılım çubukları ────────────────────────────────────────────────────────
const BarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 640px;
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 170px 1fr 250px;
  align-items: center;
  gap: 10px;
`;

const BarLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  position: relative;
  height: 12px;
  border-radius: 6px;
  background: ${({ theme }) => `${theme.colors.primary.main}11`};
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary.main},
    ${({ theme }) => theme.colors.secondary.main}
  );
`;

// Denge (1/k) işaretçisi — çubuğun İÇİNDE, çünkü kırılım ölçeği artık mutlak: bir çubuğun
// bu çizgiyi ne kadar aştığı doğrudan okunabilir bir bilgi. Eğilim grafiğindeki referans
// çizgisiyle AYNI eşik, aynı renk mantığı.
const BarMarker = styled.span`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ $pct }) => $pct}%;
  width: 2px;
  background: ${({ theme }) => theme.colors.text.muted};
  opacity: 0.7;
`;

const BarValue = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

// n ve normalize yoğunlaşma. İkisi de ana değerin altında ikincil satırda: bir çubuğun
// %83'ünün 6 kişiden mi 400 kişiden mi geldiğini görmeden okumak, gürültüyü içgörü sanmaktır.
const BarValueMeta = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  color: ${({ theme }) => theme.colors.text.muted};
  font-variant-numeric: tabular-nums;
`;

// Önceki döneme göre değişim. Renk metriğin YÖNÜNDEN gelir (katalogdaki HaPolarity):
// boş pozisyonda düşüş iyi, halef kapsamasında yükseliş iyi. Yönü "neutral" olan metrikte
// hiçbir renk yargısı verilmez — uydurma bir yargı, yargı yokluğundan kötüdür.
const BarDelta = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $tone }) => ($tone === 'good'
    ? theme.colors.auxiliar.success
    : $tone === 'bad'
      ? theme.colors.auxiliar.danger
      : theme.colors.text.muted)};
`;

// Dağılım paneli. Çubuğun ALTINDA açılır — kullanıcı hangi satırı açtığını görsel olarak
// kaybetmesin diye ayrı bir modal/drawer DEĞİL.
const DistributionPanel = styled.div`
  grid-column: 1 / -1;
  margin: 4px 0 12px 0;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  background: ${({ theme }) => `${theme.colors.primary.main}0A`};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DistRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 120px;
  align-items: center;
  gap: 10px;
`;

const DistLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme, $muted }) => ($muted ? theme.colors.text.muted : theme.colors.text.primary)};
  font-style: ${({ $muted }) => ($muted ? 'italic' : 'normal')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DistTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => `${theme.colors.primary.main}14`};
  overflow: hidden;
`;

const DistFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 4px;
  background: ${({ theme, $muted }) => ($muted ? theme.colors.text.muted : theme.colors.primary.main)};
  opacity: ${({ $muted }) => ($muted ? 0.35 : 1)};
`;

const DistValue = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const DistNote = styled.p`
  margin: 4px 0 0 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const ScaleNote = styled.p`
  margin: 14px 0 0 0;
  max-width: 640px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const SuppressedNote = styled.p`
  margin: 10px 0 0 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

export default function HrAnalyticsMetricDetailPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { code } = useParams();
  const { getCatalog, getSettings, getMetrics, getDistribution } = useHrAnalyticsApi();

  const [meta, setMeta] = useState(null);
  const [ready, setReady] = useState(false);
  const [trendCells, setTrendCells] = useState([]);
  const [breakdownScope, setBreakdownScope] = useState(null);
  const [breakdownScopes, setBreakdownScopes] = useState([]);
  const [breakdownCells, setBreakdownCells] = useState([]);
  // Açık dağılım paneli: hangi kapsam açık, verisi ne. Sayfa yüklenişinde İSTEK YOK —
  // yalnız kullanıcı bir çubuğa tıkladığında.
  const [openScopeId, setOpenScopeId] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [distributionLoading, setDistributionLoading] = useState(false);

  // Adım 1: katalog meta'sı (ad/katman/durum/kapsamlar) + ayarlardan varsayılan kırılım.
  // Hata toast'ı YOK (CLAUDE.md) — hatalar yalnız Axios interceptor'da.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getCatalog(), getSettings()]).then(([catalogRes, settingsRes]) => {
      if (cancelled) return;
      const item = (catalogRes?.data?.data ?? []).find((m) => m.code === code) || null;
      setMeta(item);
      // Tenant trend'i AYRI çizilir; kırılım seçenekleri tenant DIŞI desteklenen kapsamlardır.
      const scopes = (item?.supportedScopes ?? []).filter((s) => s !== 'tenant');
      const order = settingsRes?.data?.data?.primaryScopeOrder ?? [];
      setBreakdownScopes(scopes);
      setBreakdownScope(resolveDefaultScope(order, scopes));
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [code, getCatalog, getSettings]);

  const available = meta?.state === 'available';
  const tenantSupported = (meta?.supportedScopes ?? []).includes('tenant');

  // Adım 2: tenant trend serisi (metrik tenant kapsamını destekliyorsa).
  useEffect(() => {
    if (!available || !tenantSupported) return undefined;
    let cancelled = false;
    getMetrics({ codes: code, scope: 'tenant', grain: 'month' }).then((res) => {
      if (cancelled) return;
      const result = (res?.data?.data ?? []).find((r) => r.code === code);
      setTrendCells(result?.cells ?? []);
    });
    return () => { cancelled = true; };
  }, [available, tenantSupported, code, getMetrics]);

  // Adım 3: kırılım — seçili kapsamın hücreleri; yalnız EN SON dönem gösterilir.
  useEffect(() => {
    if (!available || !breakdownScope) return undefined;
    let cancelled = false;
    setOpenScopeId(null);
    setDistribution(null);
    getMetrics({ codes: code, scope: breakdownScope, grain: 'month' }).then((res) => {
      if (cancelled) return;
      const result = (res?.data?.data ?? []).find((r) => r.code === code);
      setBreakdownCells(result?.cells ?? []);
    });
    return () => { cancelled = true; };
  }, [available, breakdownScope, code, getMetrics]);

  // Değer biçimlendirme sezgiseli: serinin tamamı |v| <= 1.5 ise oran kabul edilir (yüzde),
  // aksi hâlde düz sayı (kadro sayıları, medyan yıllar, dakikalar...). 45 metrik için elle
  // birim tablosu tutmaktan daha dürüst bir v1 — yanılırsa değer YİNE doğrudur, yalnız
  // biçim düz sayıya düşer.
  const formatValue = useMemo(() => {
    const all = [...trendCells, ...breakdownCells]
      .map((c) => c?.value)
      .filter((v) => v !== null && v !== undefined)
      .map(Number);
    const isRatio = all.length > 0 && all.every((v) => Math.abs(v) <= 1.5);
    const pct = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
    const num = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
    return (v) => (isRatio ? pct.format(v) : num.format(v));
  }, [trendCells, breakdownCells, i18n.language]);

  // formatValue'nun sezgiseli (|v| <= 1.5 ise yüzde) tüm seri düz sayıya düşerse yüzdeyi
  // bırakır; denge eşiği ise HER ZAMAN bir orandır. Bu yüzden ayrı, koşulsuz bir yüzde
  // biçimlendirici — eşiği "0,33" diye yazmak onu okunamaz kılardı.
  const formatPercent = useMemo(() => {
    const pct = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });
    return (v) => pct.format(v);
  }, [i18n.language]);

  // Son dönem çubukları + ÖNCEKİ dönemin aynı kapsamdaki değeri. Önceki dönem ek bir istek
  // GEREKTİRMEZ: kırılım çağrısı zaten tüm dönemleri getiriyordu, sayfa yalnız sonuncusunu
  // çiziyordu. "%7,7" tek başına iyi mi kötü mü söylemez; "geçen ay %0'dı" söyler.
  const latestBreakdown = useMemo(() => {
    if (breakdownCells.length === 0) return { bars: [], suppressed: 0, prevByScope: new Map() };
    const times = [...new Set(breakdownCells.map((c) => new Date(c.periodStart).getTime()))]
      .sort((a, b) => b - a);
    const latestTime = times[0];
    const prevTime = times[1] ?? null;

    const latest = breakdownCells.filter((c) => new Date(c.periodStart).getTime() === latestTime);
    const bars = latest
      .filter((c) => c.value !== null && c.value !== undefined)
      .sort((a, b) => b.value - a.value);

    const prevByScope = new Map();
    if (prevTime !== null) {
      breakdownCells
        .filter((c) => new Date(c.periodStart).getTime() === prevTime
          && c.value !== null && c.value !== undefined)
        .forEach((c) => prevByScope.set(c.scopeId, c.value));
    }
    return { bars, suppressed: latest.length - bars.length, prevByScope };
  }, [breakdownCells]);

  // Aynı dönemin ŞİRKET GENELİ değeri — kırılımdaki her çubuğun referans noktası. Bu da
  // mevcut veriden gelir (tenant eğilim serisi zaten yüklü), ek istek yok.
  const tenantReference = useMemo(() => {
    if (latestBreakdown.bars.length === 0 || trendCells.length === 0) return null;
    const barTime = new Date(latestBreakdown.bars[0].periodStart).getTime();
    const match = trendCells.find((c) => new Date(c.periodStart).getTime() === barTime);
    return match?.value ?? null;
  }, [latestBreakdown, trendCells]);

  const maxBar = latestBreakdown.bars.length > 0
    ? Math.max(...latestBreakdown.bars.map((b) => Math.abs(b.value))) || 1
    : 1;

  const isConcentration = CONCENTRATION_CODES.has(code);

  // k = kullanımdaki kategori sayısı. Eğilim (tenant) ve kırılım aynı dönemde AYNI k'yı taşır;
  // hangisi doluysa o kullanılır (metrik tenant kapsamını desteklemiyorsa trendCells boştur).
  const categoryCount = useMemo(
    () => latestCategoryCount(trendCells) ?? latestCategoryCount(breakdownCells),
    [trendCells, breakdownCells]
  );

  // Denge = 1/k: en büyük grup payının ULAŞABİLECEĞİ EN DÜŞÜK değer. Sabit 0.5 yalnız iki
  // kategorili metriklerde (cinsiyet, yaka tipi) doğruydu; 9-Kutu'da taban %11'dir ve %50'lik
  // bir çizgi %40'lık bir yığılmayı "dengenin altında" göstererek tersini söylüyordu.
  //
  // k < 2 → çizgi YOK: tek kategori kullanımdayken değer tanımı gereği %100'dür ve "denge"
  // diye bir şey yoktur; %100'e çizgi çekmek bilgi değil gürültü olurdu.
  const balance = isConcentration && categoryCount >= 2 ? 1 / categoryCount : null;

  // Normalize yoğunlaşma: 0 = tam denge, 1 = tek kategoride toplanma. Ham yüzdenin tabanı
  // metrikten metriğe değiştiği için ("%40" 9-Kutu'da ciddi yığılma, cinsiyette İMKÂNSIZ)
  // metrikler arası tek karşılaştırılabilir sayı budur. Ham yüzde YERİNE değil, YANINDA
  // gösterilir — İK yetkilisinin tanıdığı sayı hâlâ yüzdedir.
  const normalize = (value) => (balance === null || value == null
    ? null
    : Math.min(1, Math.max(0, (value - balance) / (1 - balance))));

  // Kazanan kategorinin görünen adı. Öncelik backend'in çözdüğü ada (tenant tanımlı kayıtlar);
  // yoksa uygulamanın var olan çeviri anahtarı; o da yoksa HAM anahtar — kod göstermek,
  // hiçbir şey göstermemekten iyidir (bkz. CATEGORY_LABEL_KEYS yorumu).
  const resolveCategory = (key, label) => {
    if (label) return label;
    if (key === null || key === undefined || key === '') return null;
    const entry = CATEGORY_LABEL_KEYS[code];
    const i18nKey = typeof entry === 'function' ? entry(key) : entry?.[key];
    return i18nKey ? t(i18nKey, key) : key;
  };

  const categoryName = (cell) => resolveCategory(cell?.topCategoryKey, cell?.topCategoryLabel);

  // ── Sayıyı cümleye çeviren katman ──────────────────────────────────────────
  // Katalog artık her metriğin NEYİ saydığını taşıyor (HaUnit). Birim yoksa "none" gelir ve
  // aşağıdaki üretimler null döner — sayfa eski davranışına, düz değere düşer.
  const unitKey = meta?.unit || 'none';

  const formatCount = useMemo(() => {
    const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });
    return (v) => nf.format(v);
  }, [i18n.language]);

  // Hücrenin SOMUT gerçeği: "1 / 13 koltuk" ya da sayım metriğinde "128 çalışan".
  //
  // Neden "13 koltuktan 1'i" değil: Türkçede iyelik eki sayının son sesine göre değişiyor
  // (1'i, 2'si, 3'ü, 6'sı, 9'u...) ve şablonla üretilen her varyant er geç yanlış çekimlenirdi.
  // "1 / 13" biçimi hem dilbilgisi riski taşımıyor hem de beş dilde AYNI şablonla çalışıyor.
  //
  // Sayı ile birimin DİZİLİMİ i18n şablonunda (units.<birim>.ratio/count) — JS'te birleştirme
  // YOK. Rusçada sayının ardından gelen ad çekimlenir, İngilizcede çoğullanır; bunu kodda
  // kurmak her dile ayrı bir dal açardı.
  const factLine = (cell) => {
    const num = cell?.numerator;
    const den = cell?.denominator;
    if (num === null || num === undefined) return null;
    const key = den === null || den === undefined ? 'count' : 'ratio';
    return t(`hrAnalytics.units.${unitKey}.${key}`, {
      n: formatCount(num),
      d: den === null || den === undefined ? '' : formatCount(den),
      defaultValue: '',
    }) || null;
  };

  // Yön: metrik yükseldikçe iyiye mi gidiyor? "neutral" GERÇEK bir cevaptır (kadro sayısının
  // yüksek olması ne iyi ne kötü) — o durumda hiçbir yargı gösterilmez.
  const polarity = meta?.polarity || 'neutral';
  const deltaTone = (delta) => {
    if (polarity === 'neutral' || !delta) return 'flat';
    // Backend HaCatalogDataAccess.ToSnake ile gönderir — tek biçim (snake_case) yeterli.
    const better = polarity === 'higher_is_better' ? delta > 0 : delta < 0;
    return better ? 'good' : 'bad';
  };

  // Çubuğa tıklama: aynı kapsam açıksa kapat, değilse aç ve TEK istek at. Kapalı bir dönemin
  // dağılımı değişmediği için aynı kapsam yeniden açıldığında sunucuya tekrar gidilmez.
  const toggleDistribution = (bar) => {
    if (openScopeId === bar.scopeId) {
      setOpenScopeId(null);
      return;
    }
    setOpenScopeId(bar.scopeId);
    setDistribution(null);
    setDistributionLoading(true);
    getDistribution({
      code,
      scope: breakdownScope,
      scopeId: bar.scopeId,
      period: bar.periodStart,
    })
      .then((res) => setDistribution(res?.data?.data ?? null))
      .finally(() => setDistributionLoading(false));
  };

  const scopeOptions = useMemo(
    () => breakdownScopes.map((s) => ({ id: s, name: t(`hrAnalytics.scopes.${s}`) })),
    [breakdownScopes, t]
  );

  const metricName = t(`hrAnalytics.metricNames.${code}`, code);
  // Açıklama yoksa (çeviri girilmemiş yeni metrik) paragraf HİÇ çizilmez — kod gösterip
  // şaşırtmaktansa dürüst yokluk.
  const description = t(`hrAnalytics.metricDescriptions.${code}`, { defaultValue: '' });

  return (
    <Container>
      <Card>
        <HeadRow>
          <Button type="button" variant="ghost" onClick={() => history.push('/hr-analytics')}>
            <ArrowLeft size={16} aria-hidden="true" />
            {t('hrAnalytics.metricDetail.backToCatalog')}
          </Button>
          <Title>{metricName}</Title>
          {meta && <MetaChip>{t(`hrAnalytics.tiers.${meta.tier}`)}</MetaChip>}
          {meta?.basis?.length > 0 && <MetaChip>{`${t('hrAnalytics.basis')}: ${meta.basis.join(', ')}`}</MetaChip>}
          {/* "%7,7 iyi mi kötü mü?" sorusunun doğrudan cevabı. Yönü "neutral" olan metrikte
              çip HİÇ çizilmez — kadro sayısının yüksek olması ne iyi ne kötüdür ve uydurma
              bir yargı, yargı yokluğundan kötüdür (bkz. HaPolarity). */}
          {polarity !== 'neutral' && (
            <MetaChip data-role="metric-polarity">{t(`hrAnalytics.polarity.${polarity}`, '')}</MetaChip>
          )}
        </HeadRow>
        {description && (
          <MetricDescription data-role="metric-description">{description}</MetricDescription>
        )}
      </Card>

      {!ready ? (
        <Card><StateMessage>{t('common.loading')}</StateMessage></Card>
      ) : !meta ? (
        <Card><StateMessage>{t('hrAnalytics.metricDetail.unknownMetric')}</StateMessage></Card>
      ) : !available ? (
        <Card><StateMessage>{t(`responseErrors.${meta.reasonKey}`)}</StateMessage></Card>
      ) : (
        <>
          {tenantSupported && (
            <Card>
              <SectionTitle>{t('hrAnalytics.metricDetail.trendTitle')}</SectionTitle>
              <MetricTrendChart
                cells={trendCells}
                formatValue={formatValue}
                fileName={`metrik-${code.replace(/\./g, '-')}.png`}
                referenceValue={balance}
                referenceLabel={balance === null
                  ? null
                  : t('hrAnalytics.metricDetail.balanceLine', { value: formatPercent(balance) })}
              />
            </Card>
          )}

          {breakdownScopes.length > 0 && (
            <Card>
              <SectionTitle>{t('hrAnalytics.metricDetail.breakdownTitle')}</SectionTitle>
              <Controls>
                <GenericSelect
                  label={t('hrAnalytics.funnelPlot.scopeSelectorLabel')}
                  options={scopeOptions}
                  value={breakdownScope}
                  onChange={setBreakdownScope}
                />
              </Controls>

              {latestBreakdown.bars.length === 0 ? (
                <StateMessage>{t('hrAnalytics.metricDetail.noBreakdownData')}</StateMessage>
              ) : (
                <BarList data-role="metric-breakdown-bars">
                  {latestBreakdown.bars.map((b) => {
                    const name = isConcentration ? categoryName(b) : null;
                    const normalized = isConcentration ? normalize(b.value) : null;

                    // BİRİNCİ SATIR: somut gerçek. Eskiden burada çıplak yüzde vardı ve altında
                    // "N = 12" yazıyordu — ikisi de neyin sayıldığını söylemiyordu (dahası N,
                    // headcount.vacancy.rate gibi metriklerde paydaya EŞİT DEĞİL, bu yüzden
                    // "12'nin %7,7'si" diye yanlış okunuyordu). Artık "1 / 13 koltuk".
                    const fact = factLine(b);
                    // Birim de pay/payda da yoksa geriye düşülecek tek dürüst şey hücre
                    // büyüklüğüdür — ama "N" jargonu yerine ne olduğu yazılarak.
                    const fallback = b.n != null
                      ? t('hrAnalytics.metricDetail.recordCount', { count: formatCount(b.n) })
                      : null;

                    const prev = latestBreakdown.prevByScope.get(b.scopeId);
                    const delta = prev === undefined ? null : b.value - prev;

                    // `metaLine` — dıştaki `meta` (katalog kaydı) BİLEREK gölgelenmiyor.
                    const metaLine = [
                      formatValue(b.value),
                      tenantReference !== null
                        ? t('hrAnalytics.metricDetail.companyReference', {
                          value: formatValue(tenantReference),
                        })
                        : null,
                      normalized !== null
                        ? t('hrAnalytics.metricDetail.normalizedConcentration', {
                          value: formatPercent(normalized),
                        })
                        : null,
                    ].filter(Boolean).join(' · ');

                    const isOpen = isConcentration && openScopeId === b.scopeId;
                    const openable = isConcentration;

                    return (
                      <React.Fragment key={b.scopeId}>
                      <BarRow
                        {...(openable ? {
                          role: 'button',
                          tabIndex: 0,
                          'aria-expanded': isOpen,
                          'data-role': 'metric-breakdown-row',
                          style: { cursor: 'pointer' },
                          onClick: () => toggleDistribution(b),
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleDistribution(b);
                            }
                          },
                        } : {})}
                      >
                        <BarLabel title={b.scopeName || b.scopeId}>
                          {b.scopeName || `${t(`hrAnalytics.scopes.${b.scopeType}`)} #${String(b.scopeId || '').slice(0, 8)}`}
                        </BarLabel>
                        <BarTrack>
                          {/* Yoğunlaşmada ölçek MUTLAK (değer zaten [1/k, 1] oranı); diğer
                              metrikler kadro/yıl/dakika gibi sınırsız birimler taşıdığından
                              onlarda en uzun çubuğa göre normalize edilmeye devam edilir. */}
                          <BarFill $pct={isConcentration
                            ? Math.round(b.value * 100)
                            : Math.round((Math.abs(b.value) / maxBar) * 100)}
                          />
                          {balance !== null && (
                            <BarMarker
                              $pct={balance * 100}
                              data-role="metric-balance-marker"
                              aria-hidden="true"
                            />
                          )}
                        </BarTrack>
                        <BarValue>
                          <span>
                            {isConcentration && name
                              ? t('hrAnalytics.metricDetail.largestGroupNamed', { name })
                              : (fact || fallback || formatValue(b.value))}
                          </span>
                          {metaLine && <BarValueMeta>{metaLine}</BarValueMeta>}
                          {isConcentration && name && (fact || fallback) && (
                            <BarValueMeta>{fact || fallback}</BarValueMeta>
                          )}
                          {delta !== null && delta !== 0 && (
                            <BarDelta $tone={deltaTone(delta)}>
                              {t('hrAnalytics.metricDetail.previousPeriod', {
                                value: formatValue(prev),
                              })}
                            </BarDelta>
                          )}
                        </BarValue>
                      </BarRow>

                      {isOpen && (
                        <DistributionPanel data-role="metric-distribution">
                          {distributionLoading && <DistNote>{t('common.loading')}</DistNote>}

                          {!distributionLoading && distribution?.suppressedReason && (
                            // Genel bastırma metni ("Bu kırılım ... gösterilmiyor") burada
                            // yanlış ismi kullanırdı: ekranda kırılım değil DAĞILIM var.
                            <DistNote>{t('hrAnalytics.metricDetail.distributionSuppressed')}</DistNote>
                          )}

                          {!distributionLoading && !distribution?.suppressedReason
                            && (distribution?.rows?.length ?? 0) === 0 && (
                            <DistNote>{t('hrAnalytics.metricDetail.noDistributionData')}</DistNote>
                          )}

                          {!distributionLoading && (distribution?.rows?.length ?? 0) > 0
                            && distribution.rows.map((r, idx) => {
                              const label = r.isOther
                                ? t('hrAnalytics.metricDetail.otherCategories')
                                : (resolveCategory(r.categoryKey, r.categoryLabel) || r.categoryKey);
                              const top = distribution.rows[0].share || 1;
                              return (
                                <DistRow key={r.categoryKey || `other-${idx}`}>
                                  <DistLabel title={label} $muted={r.isOther}>{label}</DistLabel>
                                  <DistTrack>
                                    <DistFill $pct={Math.round((r.share / top) * 100)} $muted={r.isOther} />
                                  </DistTrack>
                                  <DistValue>
                                    {`${t(`hrAnalytics.units.${unitKey}.count`, {
                                      n: formatCount(r.count), defaultValue: formatCount(r.count),
                                    })} · ${formatPercent(r.share)}`}
                                  </DistValue>
                                </DistRow>
                              );
                            })}

                          {/* Birleştirme YALNIZ gizlilik eşiği yüzünden olur; olduğunda
                              kullanıcıya nedenini söylemek zorunludur — aksi hâlde eksik
                              kategorileri veri hatası sanar. */}
                          {!distributionLoading && distribution?.rows?.some((r) => r.isOther) && (
                            <DistNote data-role="metric-distribution-merged-note">
                              {t('hrAnalytics.metricDetail.mergedNote', {
                                count: distribution.rows.find((r) => r.isOther).mergedCategoryCount,
                              })}
                            </DistNote>
                          )}
                        </DistributionPanel>
                      )}
                      </React.Fragment>
                    );
                  })}
                </BarList>
              )}

              {/* Tıklanabilirlik keşfedilebilir olmalı: ipucu olmadan kullanıcı çubuğa
                  tıklanabileceğini bilemez. Yalnız dağılımı OLAN ailede gösterilir. */}
              {isConcentration && latestBreakdown.bars.length > 0 && (
                <DistNote data-role="metric-distribution-hint">
                  {t('hrAnalytics.metricDetail.distributionHint')}
                </DistNote>
              )}

              {latestBreakdown.suppressed > 0 && (
                <SuppressedNote>
                  {t('hrAnalytics.metricDetail.suppressedNote', { count: latestBreakdown.suppressed })}
                </SuppressedNote>
              )}

              {/* Ölçek dipnotu: "%75" tek başına iyi mi kötü mü söylemez. Tabanı (1/k), tavanı
                  ve paydanın "kadronun tamamı DEĞİL" olduğunu yazmadan bu sayı yanlış
                  okunuyordu — kullanıcı geri bildirimi 2026-09-03. */}
              {balance !== null && latestBreakdown.bars.length > 0 && (
                <ScaleNote data-role="metric-concentration-note">
                  {t('hrAnalytics.metricDetail.concentrationNote', {
                    floor: formatPercent(balance),
                    count: categoryCount,
                  })}
                </ScaleNote>
              )}
            </Card>
          )}
        </>
      )}
    </Container>
  );
}
